import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { MetaMarketDTO } from '../types/api';
import { WS_BASE_URL, WS_TOPICS } from '../config/websocket';

type MessageCallback<T> = (data: T) => void;

class WebSocketService {
  private client: Client | null = null;
  private marketSubscriptions: Map<number, MessageCallback<MetaMarketDTO>> = new Map();
  private eventSubscriptions: Map<number, MessageCallback<any>> = new Map();
  private allMarketsCallback: MessageCallback<MetaMarketDTO> | null = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;

  constructor() {
    this.initializeClient();
  }

  private initializeClient(): void {
    // Log the WebSocket URL for debugging
    console.log('Initializing WebSocket connection to:', WS_BASE_URL);
    
    // Create a new STOMP client
    this.client = new Client({
      // Use SockJS as the WebSocket factory
      webSocketFactory: () => {
        console.log('Creating SockJS connection to:', WS_BASE_URL);
        // Create SockJS instance with explicit options
        const sockjsOptions = {
          transports: ['websocket', 'xhr-streaming', 'xhr-polling']
        };
        const sock = new SockJS(WS_BASE_URL, null, sockjsOptions);
        
        // Add event handlers for debugging
        sock.onopen = () => console.log('SockJS connection opened successfully');
        sock.onclose = (e) => console.log('SockJS connection closed', e);
        sock.onerror = (e) => console.error('SockJS connection error:', e);
        
        return sock;
      },
      
      // Enable debug logging
      debug: (str) => {
        console.log('STOMP debug:', str);
      },
      
      // Connection parameters
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      connectionTimeout: 10000
    });

    this.client.onConnect = this.onConnect.bind(this);
    this.client.onStompError = this.onStompError.bind(this);
    this.client.onWebSocketClose = this.onWebSocketClose.bind(this);
    this.client.onWebSocketError = this.onWebSocketError.bind(this);
  }

  private onConnect(): void {
    console.log('WebSocket connected successfully');
    this.isConnected = true;
    this.reconnectAttempts = 0;
    
    // Log active subscriptions for debugging
    console.log(`Active market subscriptions: ${this.marketSubscriptions.size}`);
    console.log(`Active event subscriptions: ${this.eventSubscriptions.size}`);
    
    // Resubscribe to all topics after reconnection
    this.resubscribeAll();
  }

  private onStompError(frame: any): void {
    console.error('STOMP error:', frame);
    // Log detailed error information
    if (frame && frame.headers) {
      console.error('STOMP error headers:', frame.headers);
    }
    if (frame && frame.body) {
      console.error('STOMP error body:', frame.body);
    }
  }

  private onWebSocketClose(): void {
    console.log('WebSocket connection closed');
    this.isConnected = false;
    
    // Attempt to reconnect if max attempts not reached
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      setTimeout(() => this.connect(), 5000 * this.reconnectAttempts);
    } else {
      console.error('Max reconnect attempts reached. Please refresh the page.');
    }
  }

  private onWebSocketError(error: any): void {
    console.error('WebSocket connection error:', error);
  }

  private resubscribeAll(): void {
    // Resubscribe to market updates
    this.marketSubscriptions.forEach((callback, eventId) => {
      this.subscribeToMarketUpdates(eventId, callback);
    });

    // Resubscribe to event updates
    this.eventSubscriptions.forEach((callback, eventId) => {
      this.subscribeToEventUpdates(eventId, callback);
    });
  }

  public connect(): void {
    if (!this.client) {
      console.error('WebSocket client is null, reinitializing...');
      this.initializeClient();
    }
    
    if (this.client && !this.isConnected) {
      console.log('Activating WebSocket connection to:', WS_BASE_URL);
      this.client.activate();
    } else if (this.isConnected) {
      console.log('WebSocket already connected');
    }
  }

  public disconnect(): void {
    if (this.client && this.isConnected) {
      this.client.deactivate();
      this.isConnected = false;
    }
  }

  public subscribeToMarketUpdates(eventId: number, callback: MessageCallback<MetaMarketDTO>): void {
    if (!this.client) {
      console.error('WebSocket client not initialized');
      return;
    }

    // Store the callback for potential reconnection
    this.marketSubscriptions.set(eventId, callback);

    if (this.isConnected) {
      // Get the topic for this event
      const topic = WS_TOPICS.MARKET_UPDATES(eventId);
      console.log(`Subscribing to market updates on topic: ${topic}`);
      
      // Subscribe to event-specific market updates
      const subscription = this.client.subscribe(
        topic,
        (message: IMessage) => {
          try {
            console.log(`Received market update for event ${eventId}:`, message.body);
            const marketData: MetaMarketDTO = JSON.parse(message.body);
            callback(marketData);
          } catch (error) {
            console.error('Error parsing market update:', error);
            console.error('Raw message body:', message.body);
          }
        }
      );

      console.log(`Subscribed to market updates for event ${eventId}`);
    } else {
      console.log(`Will subscribe to market updates for event ${eventId} when connected`);
      // Try to connect if not already connected
      this.connect();
    }
  }

  public unsubscribeFromMarketUpdates(eventId: number): void {
    this.marketSubscriptions.delete(eventId);
    // The actual STOMP unsubscribe happens automatically when the component unmounts
  }

  public subscribeToAllMarketUpdates(callback: MessageCallback<MetaMarketDTO>): void {
    if (!this.client) {
      console.error('WebSocket client not initialized');
      return;
    }

    // Store the callback
    this.allMarketsCallback = callback;

    if (this.isConnected) {
      // Subscribe to all market updates
      const topic = WS_TOPICS.ALL_MARKETS;
      console.log(`Subscribing to all market updates on topic: ${topic}`);
      
      const subscription = this.client.subscribe(
        topic,
        (message: IMessage) => {
          try {
            console.log('Received market update from all markets topic:', message.body);
            const marketData: MetaMarketDTO = JSON.parse(message.body);
            callback(marketData);
          } catch (error) {
            console.error('Error parsing market update from all markets topic:', error);
            console.error('Raw message body:', message.body);
          }
        }
      );

      console.log('Subscribed to all market updates');
    } else {
      console.log('Will subscribe to all market updates when connected');
      // Try to connect if not already connected
      this.connect();
    }
  }

  public unsubscribeFromAllMarketUpdates(): void {
    this.allMarketsCallback = null;
  }

  public subscribeToEventUpdates(eventId: number, callback: MessageCallback<any>): void {
    if (!this.client) {
      console.error('WebSocket client not initialized');
      return;
    }

    // Store the callback for potential reconnection
    this.eventSubscriptions.set(eventId, callback);

    if (this.isConnected) {
      // Subscribe to specific event updates
      const subscription = this.client.subscribe(
        WS_TOPICS.EVENT_UPDATES(eventId),
        (message: IMessage) => {
          try {
            const eventData = JSON.parse(message.body);
            callback(eventData);
          } catch (error) {
            console.error('Error parsing event update:', error);
          }
        }
      );

      console.log(`Subscribed to event updates for event ${eventId}`);
    } else {
      console.log('WebSocket not connected, will subscribe on connection');
      this.connect();
    }
  }

  public unsubscribeFromEventUpdates(eventId: number): void {
    this.eventSubscriptions.delete(eventId);
    // The actual STOMP unsubscribe happens automatically when the component unmounts
  }
}

export const websocketService = new WebSocketService();
